/* ============================================================================
   Hoja modal arrastrable.

   Cumple, en este orden:
   1. Respuesta — se agarra en pointer-down y sigue al dedo desde el primer píxel.
   2. Manipulación directa — 1:1, respetando el offset de agarre.
   3. Interrumpibilidad — se puede agarrar en pleno vuelo y revertir; la animación
      arranca del valor de presentación, nunca del objetivo.
   4. Entrega de velocidad — al soltar, el resorte continúa a la velocidad exacta
      del dedo: no hay costura entre arrastrar y animar.
   5. Proyección de inercia — un flick se va, aunque el desplazamiento sea corto.
   6. Consistencia espacial — entra y sale por el mismo camino (abajo).
   7. Límite blando — al pasar del tope, rubber-band en vez de muro.
   8. Materializar — el blur y la escala del fondo se animan juntos.
   ========================================================================== */

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  SPRING,
  SpringValue,
  clampRubber,
  prefersReducedMotion,
  projectMomentum,
} from '../lib/spring'
import { VelocityTracker } from '../lib/motion'
import './sheet.css'

export interface SheetProps {
  open: boolean
  onClose: () => void
  title: string
  /** Texto bajo el título. */
  subtitle?: string
  children: React.ReactNode
}

const DISMISS_FRACTION = 0.32
const FLICK_VELOCITY = 420

export function Sheet({ open, onClose, title, subtitle, children }: SheetProps) {
  const [mounted, setMounted] = useState(open)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const scrimRef = useRef<HTMLDivElement | null>(null)
  const heightRef = useRef(0)
  const springRef = useRef<SpringValue | null>(null)
  const restoreFocus = useRef<HTMLElement | null>(null)
  const uid = useId().replace(/[^a-zA-Z0-9-]/g, '')

  if (springRef.current === null) springRef.current = new SpringValue(1, SPRING.sheet)

  /** Escribe la posición. `p` = 0 abierta, 1 fuera de pantalla. */
  const paint = useCallback((p: number) => {
    const panel = panelRef.current
    const scrim = scrimRef.current
    const h = heightRef.current || 1
    if (panel) panel.style.transform = `translate3d(0, ${p * h}px, 0)`
    if (scrim) scrim.style.opacity = String(Math.max(0, Math.min(1, 1 - p)))
  }, [])

  useEffect(() => {
    const spring = springRef.current!
    const off = spring.onChange((value) => {
      paint(value)
      if (value > 0.999 && spring.target >= 1) setMounted(false)
    })
    return off
  }, [paint])

  useEffect(() => {
    if (open) {
      restoreFocus.current = document.activeElement as HTMLElement | null
      setMounted(true)
    } else if (mounted) {
      springRef.current!.to(1, SPRING.sheet)
      if (prefersReducedMotion()) setMounted(false)
    }
  }, [open, mounted])

  useLayoutEffect(() => {
    if (!mounted) return
    const panel = panelRef.current
    if (!panel) return
    heightRef.current = panel.offsetHeight
    if (open) {
      // Arranca fuera de pantalla y sube. Sin salto: pintamos antes de animar.
      if (springRef.current!.value >= 1) {
        springRef.current!.set(1)
        paint(1)
      }
      const raf = requestAnimationFrame(() => springRef.current!.to(0, SPRING.sheet))
      return () => cancelAnimationFrame(raf)
    }
  }, [mounted, open, paint])

  // Bloqueo de scroll y Escape mientras está abierta.
  useEffect(() => {
    if (!mounted) return
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prevOverflow
      document.removeEventListener('keydown', onKey)
    }
  }, [mounted, onClose])

  // Foco: entra al panel, se atrapa dentro, y regresa al cerrar.
  useEffect(() => {
    if (!mounted || !open) return
    const panel = panelRef.current
    panel?.focus({ preventScroll: true })
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !panel) return
      /* Sólo lo que de verdad puede recibir foco AHORA.
         El selector por sí solo también atrapaba lo que vive dentro de un
         `<details>` cerrado —la tabla espejo de cada gráfica va cerrada por
         omisión—: `last` apuntaba a uno de esos nodos, la comparación con
         `document.activeElement` nunca era cierta, y el Tab salía de la hoja a la
         página de atrás con la hoja todavía abierta encima.

         Medir `getClientRects()` NO alcanza: el contenedor de la tabla conserva
         su caja (333×320 px medidos) aunque el `<details>` esté cerrado, y aun
         así el navegador se niega a enfocarlo. La condición que sí distingue es
         la pertenencia: cualquier cosa dentro de un `<details>` cerrado queda
         fuera del orden de tabulación, salvo su propio `<summary>`. */
      const focusables = Array.from(
        panel.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => {
        if (el.getClientRects().length === 0) return false
        const details = el.closest('details')
        if (details && !details.open && el.tagName !== 'SUMMARY') return false
        return true
      })
      if (focusables.length === 0) return
      const first = focusables[0]!
      const last = focusables[focusables.length - 1]!
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      restoreFocus.current?.focus?.()
    }
  }, [mounted, open])

  /* --- Arrastre ---------------------------------------------------------- */
  useEffect(() => {
    if (!mounted) return
    const handle = panelRef.current?.querySelector<HTMLElement>('[data-drag-handle]')
    if (!handle) return

    const tracker = new VelocityTracker()
    let pointerId: number | null = null
    let grabOffset = 0
    let committed = false
    const THRESHOLD = 6

    const onDown = (event: PointerEvent) => {
      if (pointerId !== null) return
      if (event.pointerType === 'mouse' && event.button !== 0) return
      // El botón de cerrar no arrastra: capturar el puntero aquí le comería el click.
      if ((event.target as Element | null)?.closest('[data-no-drag]')) return
      pointerId = event.pointerId
      const h = heightRef.current || 1
      // Offset de agarre: dónde tomó la hoja, no su centro.
      grabOffset = event.clientY - springRef.current!.value * h
      committed = false
      tracker.reset(event.clientY, event.timeStamp)
      handle.setPointerCapture(event.pointerId)
    }

    const onMove = (event: PointerEvent) => {
      if (pointerId !== event.pointerId) return
      tracker.add(event.clientY, event.timeStamp)
      const h = heightRef.current || 1
      const raw = (event.clientY - grabOffset) / h
      if (!committed) {
        if (Math.abs(raw - springRef.current!.value) * h < THRESHOLD) return
        committed = true
      }
      // Arriba del tope: resistencia progresiva, nunca un alto duro.
      const next = clampRubber(raw, 0, 1, 1)
      springRef.current!.track(next)
    }

    const finish = (event: PointerEvent) => {
      if (pointerId !== event.pointerId) return
      pointerId = null
      if (handle.hasPointerCapture(event.pointerId)) handle.releasePointerCapture(event.pointerId)
      if (!committed) return
      committed = false

      const h = heightRef.current || 1
      const velocityPx = tracker.velocity(event.timeStamp)
      const current = springRef.current!.value
      // ¿A dónde va el gesto, no dónde se soltó?
      const projectedPx = current * h + projectMomentum(velocityPx)
      const projected = projectedPx / h

      const dismiss =
        velocityPx > FLICK_VELOCITY ||
        (velocityPx > -FLICK_VELOCITY && projected > DISMISS_FRACTION)

      if (dismiss) {
        springRef.current!.to(1, { ...SPRING.momentum, velocity: velocityPx / h })
        onClose()
      } else {
        springRef.current!.to(0, { ...SPRING.sheet, velocity: velocityPx / h })
      }
    }

    handle.addEventListener('pointerdown', onDown)
    handle.addEventListener('pointermove', onMove)
    handle.addEventListener('pointerup', finish)
    handle.addEventListener('pointercancel', finish)
    return () => {
      handle.removeEventListener('pointerdown', onDown)
      handle.removeEventListener('pointermove', onMove)
      handle.removeEventListener('pointerup', finish)
      handle.removeEventListener('pointercancel', finish)
    }
  }, [mounted, onClose])

  if (!mounted || typeof document === 'undefined') return null

  return createPortal(
    <div className="sheet-root">
      <div className="sheet__scrim" ref={scrimRef} onPointerDown={onClose} aria-hidden="true" />
      <div
        className="sheet__panel"
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${uid}-title`}
        tabIndex={-1}
      >
        <div className="sheet__grabber" data-drag-handle>
          <span className="sheet__grabber-bar" aria-hidden="true" />
          <div className="sheet__titles">
            <h2 className="t-title2" id={`${uid}-title`}>
              {title}
            </h2>
            {subtitle ? <p className="t-footnote ink-3">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            className="sheet__close"
            onClick={onClose}
            aria-label="Cerrar"
            data-no-drag
          >
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
              <path
                d="M3 3l8 8M11 3l-8 8"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                fill="none"
              />
            </svg>
          </button>
        </div>
        {/* Enfocable: es un contenedor con scroll cuyo contenido puede no tener
            nada enfocable, y sin `tabIndex` no habría forma de desplazarlo con
            teclado. */}
        <div
          className="sheet__content"
          tabIndex={0}
          role="group"
          aria-labelledby={`${uid}-title`}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body,
  )
}
